import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { NavBarElement } from '../../../pages/platform/components/nav-bar-element.enum'
import { AdminFeaturesIcon } from './icons/AdminFeaturesIcon'

interface AdminFeaturesDropdownProps {
  isActive: boolean
  onItemClick: () => void
}

export const AdminFeaturesDropdown: React.FC<AdminFeaturesDropdownProps> = ({
  isActive,
  onItemClick
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const adminFeatures = [
    {
      element: NavBarElement.Experts,
      title: "Experts",
      path: "/platform-experts/experts",
      description: "Gestione esperti"
    },
    {
      element: NavBarElement.Analytics,
      title: "Analytics", 
      path: "/platform-experts/analytics",
      description: "Statistiche e report"
    },
    {
      element: NavBarElement.Appointements,
      title: "Appuntamenti",
      path: "/platform-experts/appointments", 
      description: "Gestione appuntamenti"
    }
  ]

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  const handleItemClick = (path: string) => {
    navigate(path)
    setIsOpen(false)
    onItemClick()
  }

  const handleKeyPress = (event: React.KeyboardEvent, path: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleItemClick(path)
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Main button */}
      <div
        className={`
          relative group h-[90px] md:h-[48px] min-w-[60px] md:w-full flex flex-col items-center justify-center cursor-pointer
          transition-all duration-200 ease-in-out
          ${isActive ? 'scale-110' : 'hover:scale-105 active:scale-95'}
          ${isActive ? 'opacity-100' : 'hover:opacity-90'}
          touch-manipulation select-none
          md:hover:scale-105 md:active:scale-95
        `}
        onClick={handleToggle}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
          }
        }}
        tabIndex={0}
        role="button"
        aria-label="Admin Features"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Icon container */}
        <div className="mb-[6px] md:mb-[10px] h-[22px] relative">
          <AdminFeaturesIcon 
            isActive={isActive} 
            className="transition-transform duration-200 group-hover:scale-110" 
          />
        </div>
        
        {/* Title */}
        <div className={`
          font-bold text-[10px] md:text-[11px] leading-[14px] md:leading-[16px] text-white
          transition-all duration-200 text-center
          ${isActive ? 'text-white' : 'group-hover:text-gray-200'}
        `}>
          Admin
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="
          absolute 
          left-0 md:left-full 
          ml-0 md:ml-2
          right-4 md:right-auto
          bottom-0 md:top-0 
          mb-0 md:mb-0
          bg-gray-800 text-white rounded-lg shadow-lg
          min-w-[200px] z-[999999]
          border border-gray-700
        ">
          {adminFeatures.map((feature) => (
            <div
              key={feature.element}
              className="
                px-4 py-3 cursor-pointer
                bg-gray-800
                hover:bg-gray-700 transition-colors duration-200
                border-b border-gray-700 last:border-b-0
              "
              onClick={() => handleItemClick(feature.path)}
              onKeyPress={(e) => handleKeyPress(e, feature.path)}
              tabIndex={0}
              role="button"
              aria-label={`${feature.title} - ${feature.description}`}
            >
              <div className="font-semibold text-sm">{feature.title}</div>
              <div className="text-xs text-gray-300 mt-1">{feature.description}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
} 